import { vi } from "date-fns/locale"
import dayjs from "dayjs"
import { Calendar as CalendarIcon, CloseCircle } from "iconsax-reactjs"
import * as React from "react"

import { Button } from "@/components/ui"
import { Calendar } from "@/components/ui"
import { Input } from "@/components/ui"
import { Label } from "@/components/ui"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui"
import { cn } from "@/modules/helpers/hooks/shared/utils"

function formatDate(date: Date | undefined) {
  if (!date) {
    return ""
  }

  const day = date.getDate().toString().padStart(2, "0")
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const year = date.getFullYear()

  return `${day}/${month}/${year}`
}

function isValidDate(date: Date | undefined) {
  if (!date) {
    return false
  }
  return !isNaN(date.getTime())
}

function parseDateString(dateString: string): Date | undefined {
  if (!dateString) return undefined

  // Parse DD/MM/YYYY or DD-MM-YYYY format
  const parts = dateString.split(/[/-]/)
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10)
    const month = parseInt(parts[1], 10) - 1 // Month is 0-indexed
    const year = parseInt(parts[2], 10)
    const date = new Date(year, month, day)
    if (isValidDate(date)) {
      return date
    }
  }

  // Fallback: try to parse with dayjs for other formats
  const dayjsDate = dayjs(dateString, "DD/MM/YYYY")
  if (dayjsDate.isValid()) {
    return dayjsDate.toDate()
  }

  return undefined
}

export interface DateRange {
  from: string | undefined
  to: string | undefined
}

interface DatePickerRangeProps {
  value: DateRange
  onChange: (range: DateRange) => void
  labelFrom?: string
  labelTo?: string
  label?: string
  className?: string
}

export function DatePickerRange({ value, onChange, label, className }: DatePickerRangeProps) {
  const [open, setOpen] = React.useState(false)
  const [dateFrom, setDateFrom] = React.useState<Date | undefined>(
    value.from ? parseDateString(value.from) : undefined
  )
  const [dateTo, setDateTo] = React.useState<Date | undefined>(
    value.to ? parseDateString(value.to) : undefined
  )
  const [month, setMonth] = React.useState<Date | undefined>(
    value.from ? parseDateString(value.from) : new Date()
  )

  // State tạm cho việc chọn trong popover
  const [selectCurrent, setSelectCurrent] = React.useState<{ from?: Date; to?: Date }>({})
  // Ref để track trạng thái chọn ngay lập tức
  const selectCurrentRef = React.useRef<{ from?: Date; to?: Date }>({})
  // Track xem đã bắt đầu chọn mới chưa
  const hasStartedNewSelection = React.useRef(false)

  // Sync with prop value changes
  React.useEffect(() => {
    const newDateFrom = value.from ? parseDateString(value.from) : undefined
    const newDateTo = value.to ? parseDateString(value.to) : undefined
    setDateFrom(newDateFrom)
    setDateTo(newDateTo)
    if (newDateFrom) {
      setMonth(newDateFrom)
    }
  }, [value])

  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      // Khi mở popover: hiển thị giá trị cũ và reset flag
      setSelectCurrent({ from: dateFrom, to: dateTo })
      selectCurrentRef.current = { from: dateFrom, to: dateTo }
      hasStartedNewSelection.current = false
    } else {
      // Nếu đang chọn dở (đã bắt đầu selection mới và from = to), không cho đóng
      const { from, to } = selectCurrentRef.current
      if (hasStartedNewSelection.current && from && to && from.getTime() === to.getTime()) {
        return
      }
    }
    setOpen(newOpen)
  }

  const handleRangeSelect = (range: { from?: Date; to?: Date } | undefined) => {
    if (!range) {
      setSelectCurrent({})
      selectCurrentRef.current = {}
      hasStartedNewSelection.current = false
      return
    }

    // Nếu chưa bắt đầu chọn mới (đang hiển thị giá trị cũ)
    // và click vào ngày khác với range hiện tại → reset và bắt đầu chọn mới
    if (!hasStartedNewSelection.current && dateFrom && dateTo) {
      const clickedDate = range.from
      const isClickingOldRange =
        clickedDate &&
        (clickedDate.getTime() === dateFrom.getTime() ||
          clickedDate.getTime() === dateTo.getTime() ||
          (clickedDate > dateFrom && clickedDate < dateTo))

      if (!isClickingOldRange) {
        // Click vào ngày mới, bắt đầu selection mới
        hasStartedNewSelection.current = true
        setSelectCurrent(range)
        selectCurrentRef.current = range
        return
      }
    }

    // Đánh dấu đã bắt đầu selection
    hasStartedNewSelection.current = true

    // Cập nhật selectCurrent để hiển thị UI
    setSelectCurrent(range)
    // Cập nhật selectCurrentRef để track việc chọn mới
    selectCurrentRef.current = range

    // Chỉ commit vào state chính và đóng popover khi đã chọn đủ 2 ngày KHÁC NHAU
    const { from, to } = selectCurrentRef.current
    if (from && to && from.getTime() !== to.getTime()) {
      const result = {
        from: formatDate(from),
        to: formatDate(to)
      }

      setDateFrom(from)
      setDateTo(to)
      onChange(result)
      setOpen(false)
      hasStartedNewSelection.current = false
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    setDateFrom(undefined)
    setDateTo(undefined)
    onChange({ from: undefined, to: undefined })
  }

  const hasValue = dateFrom || dateTo

  return (
    <div data-testid="date-picker-range" className={cn("flex flex-col gap-2", className)}>
      {!!label && <Label className="px-1 text-xs">{label}</Label>}

      <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
        <PopoverTrigger asChild>
          <div className="relative w-full">
            <Input
              id="date-range"
              value={
                dateFrom && dateTo
                  ? `${formatDate(dateFrom)} - ${formatDate(dateTo)}`
                  : dateFrom
                    ? formatDate(dateFrom)
                    : ""
              }
              placeholder="DD/MM/YYYY - DD/MM/YYYY"
              className={cn("bg-white text-sm", hasValue ? "pr-16" : "pr-10")}
              readOnly
            />
            {hasValue && (
              <Button
                type="button"
                variant="ghost"
                className="absolute top-1/2 right-9 size-7 -translate-y-1/2 p-0"
                onClick={handleClear}
              >
                <CloseCircle className="size-5 text-gray-400" variant="Bold" />
                <span className="sr-only">Xóa</span>
              </Button>
            )}
            <Button
              id="date-picker-range"
              variant="ghost"
              className="absolute top-1/2 right-2 size-6 -translate-y-1/2"
            >
              <CalendarIcon className="size-5" />
              <span className="sr-only">Chọn khoảng thời gian</span>
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto overflow-hidden p-0"
          align="start"
          alignOffset={-8}
          sideOffset={10}
          onInteractOutside={(e) => {
            // Ngăn đóng popover khi đang chọn dở (đã bắt đầu selection mới và from = to)
            const { from, to } = selectCurrentRef.current
            if (hasStartedNewSelection.current && from && to && from.getTime() === to.getTime()) {
              e.preventDefault()
            }
          }}
          onEscapeKeyDown={(e) => {
            // Ngăn đóng popover bằng ESC khi đang chọn dở
            const { from, to } = selectCurrentRef.current
            if (hasStartedNewSelection.current && from && to && from.getTime() === to.getTime()) {
              e.preventDefault()
            }
          }}
        >
          <Calendar
            mode="range"
            selected={{ from: selectCurrent.from, to: selectCurrent.to }}
            captionLayout="dropdown"
            month={month}
            locale={vi}
            onMonthChange={setMonth}
            onSelect={handleRangeSelect}
            numberOfMonths={1}
            formatters={{
              formatMonthDropdown: (date) => date.toLocaleString("vi-VN", { month: "long" })
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
